import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { HelpCircle, Mail, ChevronDown } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

export default function FAQ() {
    useSEO({
        title: 'Frequently asked questions — Linguistic Quest',
        description: 'Answers to common questions about Linguistic Quest: how the puzzles work, age suitability, ad-free paid plans, child-safety policy, and how to get help.',
        path: '/faq',
    });
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(new Set([0]));

    useEffect(() => {
        api.get('/help/faq').then(({ data }) => setItems(data.faq || [])).catch(() => setItems([]));
    }, []);

    const toggle = (i) => setOpen((prev) => {
        const n = new Set(prev);
        if (n.has(i)) n.delete(i); else n.add(i);
        return n;
    });

    return (
        <main className="max-w-3xl mx-auto px-6 py-12" data-testid="page-faq">
            <div className="flex items-center gap-3">
                <HelpCircle size={28} className="text-lq-purple-500"/>
                <h1 className="font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Frequently asked questions</h1>
            </div>
            <p className="mt-2 text-lq-ink-700">Quick answers to the things families ask us most. Something missing? <Link to="/help" className="text-lq-purple-700 hover:underline">Ask us directly.</Link></p>

            <ul className="mt-8 space-y-2" data-testid="faq-list">
                {items.map((it, i) => {
                    const isOpen = open.has(i);
                    return (
                        <li key={i} className="bg-white border border-lq-ink-100 rounded-2xl overflow-hidden">
                            <button onClick={() => toggle(i)} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-lq-paper" data-testid={`faq-q-${i}`}>
                                <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''} text-lq-purple-500`}/>
                                <span className="font-display font-semibold text-lq-ink-900">{it.q}</span>
                            </button>
                            {isOpen && (
                                <div className="px-5 pb-5 pt-0 text-lq-ink-700 leading-relaxed" data-testid={`faq-a-${i}`}>{it.a}</div>
                            )}
                        </li>
                    );
                })}
            </ul>

            <div className="mt-10 rounded-2xl bg-lq-peach-100 border border-lq-peach-300 p-6 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                    <h2 className="font-display text-lg font-semibold text-lq-ink-900 inline-flex items-center gap-2"><Mail size={18}/> Still stuck?</h2>
                    <p className="mt-1 text-sm text-lq-ink-700">Drop us a line — a human reads every email.</p>
                </div>
                <Link to="/help" className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-lq-purple-500 text-white font-semibold" data-testid="faq-contact-cta">Contact support</Link>
            </div>
        </main>
    );
}
